import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const defaultUser = {
  email: null,
  firstName: null,
  lastName: null,
  isVerified: null,
  phoneNumber: null,
  role: [],
  adminRoles: [],
  tokenIssuedAt: null,
  _id: "",
  userPhoto: null,
  address: null,
  emergencyContact: null,
  activeRole: null,
  approvalStatus: null,
};

const defaultAuthContextProps = {
  token: null,
  isAuthenticated: false,
  authenticate: (token, userData) => {},
  logout: async () => {},
  setAuthToken: () => {},
  setUser: (user) => {},
  updateUser: (updates) => {},
  user: defaultUser,
  isLoading: true,
};

export const AuthContext = createContext(defaultAuthContextProps);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const sessionTimeoutRef = useRef(null);

  const [authToken, setAuthToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(null);
  const [user, setUser] = useState(defaultUser);

  const clearSessionTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  };

  const startSessionTimeout = useCallback((token) => {
    try {
      const { exp } = JSON.parse(atob(token.split(".")[1]));
      const expiresIn = exp * 1000 - Date.now();

      if (expiresIn <= 0) return;

      clearSessionTimeout();
      sessionTimeoutRef.current = setTimeout(logout, expiresIn);
    } catch {
      logout();
    }
  }, []);

  const authenticate = useCallback(
    (token, userData, newRefreshToken) => {
      setAuthToken(token);
      setUser(userData);
      if (newRefreshToken) setRefreshToken(newRefreshToken);

      localStorage.setItem("authToken", token);
      localStorage.setItem("user", JSON.stringify(userData));
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }

      axios.defaults.headers.Authorization = `Bearer ${token}`;
      startSessionTimeout(token);
    },
    [startSessionTimeout]
  );

  const logout = useCallback(() => {
    setAuthToken(null);
    setRefreshToken(null);
    setUser(defaultUser);

    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("eventData");

    axios.defaults.headers.Authorization = "";
    clearSessionTimeout();

    navigate("/");
  }, [navigate]);

  useEffect(() => {
    axios.defaults.baseURL = process.env.REACT_APP_NODE_API;

    const hydrate = async () => {
      const token = localStorage.getItem("authToken");
      const storedUser = localStorage.getItem("user");
      const storedRefresh = localStorage.getItem("refreshToken");

      if (!token || !storedUser) {
        setIsLoading(false);
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);

        setAuthToken(token);
        setRefreshToken(storedRefresh);
        setUser(parsedUser);

        axios.defaults.headers.Authorization = `Bearer ${token}`;
        startSessionTimeout(token);
      } catch (e) {
        // console.log(e);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    hydrate();

    return clearSessionTimeout;
  }, [logout, startSessionTimeout]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config;

        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;

          const storedRefresh = localStorage.getItem("refreshToken");
          if (!storedRefresh) {
            logout();
            return Promise.reject(error);
          }

          try {
            const res = await axios.post("/user/refresh-token", {
              refreshToken: storedRefresh,
            });

            const { token, refreshToken: newRefresh } = res.data.data;

            authenticate(token, user, newRefresh);
            original.headers.Authorization = `Bearer ${token}`;

            return axios(original);
          } catch {
            logout();
          }
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [authenticate, logout, user]);

  const value = {
    user,
    setUser,
    token: authToken,
    isAuthenticated: !!authToken && true,
    isLoading,
    authenticate,
    logout,
    updateUser: (updates) => {
      setUser((prev) => {
        const next = { ...prev, ...updates };
        localStorage.setItem("user", JSON.stringify(next));
        return next;
      });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
